import clsx from "clsx";
import LanguageSwitcher from "../../components/LanguageSwitcher/LanguageSwitcher";
import InfoBlock from "../../components/InfoBlock/InfoBlock";
import { useTranslation } from "react-i18next";
import { ReactElement } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import { MdDarkMode, MdLightMode } from "react-icons/md";
import { ICON_SIZES } from "../../helpers/constants";
import ButtonGroup from "../../components/ButtonGroup/ButtonGroup";
import LoginLogo from "../../components/LoginFlowLogo/LoginLogo";
import { Link } from "react-router-dom";
import LoadingWithText from "../../components/LoadingWithText/LoadingWithText";
import { motion } from "framer-motion";
import LoginBackground from "./LoginBackground";

interface ILoginFlowLayout {
  children: ReactElement;
  title?: any;
  description?: any;
  isLoading?: boolean;
}

const LoginFlowLayout: React.FC<ILoginFlowLayout> = ({
  children,
  title,
  description,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const { isEnabled, setEnabled } = useDarkMode();

  if (isLoading) {
    return (
      <div
        className={clsx([
          "flex",
          "justify-center",
          "w-full",
          "items-center",
          "flex-grow",
          "h-screen",
        ])}
      >
        <LoadingWithText />
      </div>
    );
  }

  return (
    <>
      <LoginBackground />

      <div
        className={clsx([
          "flex",
          "w-full",
          "flex-row",
          "min-h-[90vh]",
          "md:min-h-[100vh]",
          "md:max-h-[100vh]",
          "md:h-[100vh]",
          "md:overflow-hidden",
          "relative",
          "py-12",
          "md:py-0",
        ])}
      >
        <div
          className={clsx([
            "md:w-1/2",
            "hidden",
            "md:flex",
            "items-center",
            "flex-col",
            "justify-center",
            // "md:bg-gray-800",
            "bg-gray-50/0 dark:bg-gray-900/50",
            "gap-5",
            "md:gap-0",
          ])}
        >
          <motion.div
            className="dark:text-white text-black"
            initial={{
              scale: 0.3,
              opacity: 0.2,
            }}
            animate={{
              scale: 1,
              opacity: 1,
            }}
            transition={{
              duration: 1.5,
              type: "spring",
              stiffness: 400,
              damping: 17,
            }}
          >
            <LoginLogo />
          </motion.div>
          <motion.div
            className={clsx([
              "text-gray-700 md:text-white",
              "hidden",
              // "md:block",
              "[writing-mode:vertical-lr]",
              "md:[writing-mode:unset]",
              "text-sm",
              "text-center",
              "mx-auto",
              "opacity-70",
            ])}
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            transition={{
              delay: 0.3,
              duration: 1,
            }}
          >
            by
            <strong className="mx-1">CALIROJAS DEV.</strong>
          </motion.div>
          <div className={clsx([])}>
            {/* <InstallPWAButton classNames="mt-10" /> */}
          </div>
        </div>
        <div
          className={clsx([
            "w-full md:w-1/2",
            "flex",
            "justify-start",
            "md:p-0",
            "flex-col",
            "md:overflow-auto",
            "md:max-w-[550px]",
          ])}
        >
          <div
            className={clsx([
              "flex",
              "flex-col",
              "w-full",
              "p-6",
              "md:p-4",
              "gap-10",
              "md:bg-transparent",
            ])}
          >
            <div
              className={clsx([
                "md:flex",
                "w-full",
                "justify-between",
                // "text-white",
                "md:text-black",
                // "dark:text-white",
                "hidden",
              ])}
            >
              <ButtonGroup
                align="left"
                buttons={[
                  {
                    icon: (
                      <MdDarkMode
                        size={ICON_SIZES.MD}
                        className="text-purple-400"
                      />
                    ),
                    hide: isEnabled,
                    onClick: () => setEnabled(!isEnabled),
                    className: "!shadow-none !border-0 !bg-transparent",
                  },
                  {
                    icon: (
                      <MdLightMode
                        size={ICON_SIZES.MD}
                        className="text-yellow-400"
                      />
                    ),
                    hide: !isEnabled,
                    onClick: () => setEnabled(!isEnabled),
                    className: "!shadow-none !border-0 !bg-transparent",
                  },
                ]}
              />
              <LanguageSwitcher />
            </div>
            <div
              className={clsx([
                "flex",
                "flex-col",
                "w-full",
                "justify-center",
                "md:hidden",
                "gap-0",
                "my-4",
                "text-white",
              ])}
            >
              <LoginLogo />
              <div
                className={clsx([
                  "text-xs",
                  "text-center",
                  "w-full",
                  "mx-auto",
                  "opacity-80",
                  "hidden",
                ])}
              >
                by
                <strong className="mx-1">CALIROJAS DEV.</strong>
              </div>
              <div
                className={clsx([
                  "hidden",
                  "flex",
                  "items-center",
                  "justify-center",
                  "w-full",
                ])}
              >
                {/* <InstallPWAButton classNames="mt-4" /> */}
              </div>
            </div>
          </div>
          <motion.div
            className={clsx([
              "flex",
              "flex-col",
              "gap-6",
              "w-full",
              "md:w-auto",
              "p-6",
              "px-10",
              "md:px-6",
            ])}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            {[!!title || !!description].includes(true) === true && (
              <div className={clsx(["flex", "gap-0", "flex-col", "w-full"])}>
                <h1
                  className={clsx(["text-base", "md:text-lg", "font-medium"])}
                >
                  {title}
                </h1>
                {description && (
                  <p className={clsx(["text-sm", "md:text-base"])}>
                    {description}
                  </p>
                )}
              </div>
            )}
            {children}
            <div
              className={clsx([
                "flex",
                "w-full",
                "flex-col",
                "gap-2",
                "hidden",
              ])}
            >
              <InfoBlock
                displayMobileIcon
                title={t("active_development.title")}
                message={t("active_development.description")}
              />
              <p className="text-xs">{t("active_development.note")}</p>
            </div>
            <div
              className={clsx([
                "flex",
                "w-full",
                "justify-between",
                // "text-white",
                "md:text-black",
                // "dark:text-white",
                "md:hidden",
                "pt-16",
              ])}
            >
              <ButtonGroup
                align="left"
                buttons={[
                  {
                    icon: (
                      <MdDarkMode
                        size={ICON_SIZES.MD}
                        className="text-purple-400"
                      />
                    ),
                    hide: isEnabled,
                    onClick: () => setEnabled(!isEnabled),
                    className: "!shadow-none !border-0 !bg-transparent",
                  },
                  {
                    icon: (
                      <MdLightMode
                        size={ICON_SIZES.MD}
                        className="text-yellow-400"
                      />
                    ),
                    hide: !isEnabled,
                    onClick: () => setEnabled(!isEnabled),
                    className: "!shadow-none !border-0 !bg-transparent",
                  },
                ]}
              />
              <LanguageSwitcher />
            </div>
            <div
              className={clsx([
                "flex",
                "flex-grow",
                "flex-shrink-0",
                "items-end",
                "md:justify-end",
                "justify-center",
                "text-xs",
                "md:text-sm",
                "md:mt-10",
                "gap-4",
              ])}
            >
              <Link to="/privacy-policy" className="underline hover:opacity-90">
                Política de Privacidad
              </Link>
              <Link
                to="/terms-and-conditions"
                className="underline hover:opacity-90"
              >
                Términos y Condiciones
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default LoginFlowLayout;
